import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  AdminOrganizationConfigValues,
  AdminOrganizationConfig,
} from 'src/app/admin/models/organization.model';
import {
  AdminOrganizationConfigApiError,
  AdminOrganizationConfigApiErrorTypes,
} from './admin-organization-config.errors';
import omitBy from 'lodash-es/omitBy';
import isNull from 'lodash-es/isNull';
const BASE_URL = `${environment.apiConfig.url}/admin/organization-configs`;
const [defaultDeploymentSubdomain] = environment.deploymentSubdomains;

@Injectable()
export class AdminOrganizationConfigService {
  constructor(private http: HttpClient) {}

  convertConfigToUiValues(config: AdminOrganizationConfig) {
    const values = Object.assign({}, config.values, {
      DialogflowServiceAccount: '',
    }) as any;

    if (config.values.DialogflowServiceAccount) {
      values.DialogflowServiceAccount = JSON.stringify(
        config.values.DialogflowServiceAccount,
        null,
        2,
      );
    }

    return {
      ...values,
      defaultDomain: config.defaultDomain,
      // for backwards compatibility, `defaultSubdomain` starts with NULL. So, we set our default subdomain
      deploymentSubdomain: config.deploymentSubdomain ?? defaultDeploymentSubdomain.subdomain,
    };
  }

  createNewOrganizationConfigData(formData: any): Partial<AdminOrganizationConfig> {
    const config = {} as Partial<AdminOrganizationConfig>;

    if (formData.DialogflowServiceAccount) {
      formData.DialogflowServiceAccount = JSON.parse(formData.DialogflowServiceAccount);
    } else {
      formData.DialogflowServiceAccount = null;
    }

    const dropAttributes = ['defaultDomain', 'deploymentSubdomain'];
    const values = omitBy(
      formData,
      (value, key) => dropAttributes.includes(key) || isNull(value),
    ) as AdminOrganizationConfigValues;
    if (values) {
      config.values = values;
    }

    if (formData.defaultDomain) {
      config.defaultDomain = formData.defaultDomain;
    }

    config.deploymentSubdomain = formData.deploymentSubdomain;

    return config;
  }

  addOrganizationConfig(
    organizationId: number,
    config: Partial<AdminOrganizationConfig>,
  ): Observable<AdminOrganizationConfig> {
    const url = BASE_URL;
    const data = {
      organizationId,
      defaultDomain: config.defaultDomain,
      deploymentSubdomain: config.deploymentSubdomain,
      values: config.values,
    };

    return this.http
      .post<AdminOrganizationConfig>(url, data)
      .pipe(
        catchError((err) =>
          throwError(
            new AdminOrganizationConfigApiError(
              AdminOrganizationConfigApiErrorTypes.CreateOrganizationConfigError,
              err,
            ),
          ),
        ),
      );
  }

  getOrganizationConfig(organizationId: number): Observable<AdminOrganizationConfig> {
    const url = `${BASE_URL}/${organizationId}`;

    return this.http
      .get<AdminOrganizationConfig>(url)
      .pipe(
        catchError((err) =>
          throwError(
            new AdminOrganizationConfigApiError(
              AdminOrganizationConfigApiErrorTypes.OrganizationConfigNotFoundError,
              err,
            ),
          ),
        ),
      );
  }

  deleteOrganizationConfig(organizationId: number): Observable<any> {
    const url = `${BASE_URL}/${organizationId}`;

    return this.http
      .delete(url)
      .pipe(
        catchError((err) =>
          throwError(
            new AdminOrganizationConfigApiError(
              AdminOrganizationConfigApiErrorTypes.DeleteOrganizationConfigError,
              err,
            ),
          ),
        ),
      );
  }
}
